import {useEffect, useRef} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {STATUSES} from '@chekin/core';
import {useSettleWatchdog} from '@chekin/shared';
import {useIsOffline, useRetryAppLoad} from 'hooks';
import {isRecoveryPathname} from 'routes/constants';
import {useChekinSDK} from '../../context/ChekinSDK';
import {useReservation} from '../../context/reservation';
import {useSummary} from '../../context/summary';
import {SPLASH_TIMING} from '../SplashScreen/constants';
import ErrorView from '../../pages/ErrorView';
import {LoadErrorView} from '../../pages/LoadErrorView';
import {OfflineView} from '../../pages/OfflineView';
import {ReservationUnavailableView} from '../../pages/ReservationUnavailableView';

function ConnectionErrorsHandler() {
  const retryAppLoad = useRetryAppLoad();
  const {pathname} = useLocation();
  const {
    isAuthInitializationError,
    isThrottled,
    isSuccessSDK,
    hasToken: hasTokenInUrl,
    foundReservationId,
  } = useChekinSDK();
  const {isNotFound: isReservationNotFound} = useReservation();
  const {summaryStatus} = useSummary();
  const isOffline = useIsOffline();

  const isAuthPending = !isSuccessSDK && !isAuthInitializationError;
  const isRecoveryRoute = isRecoveryPathname(pathname);

  const hasCheckIn = Boolean(foundReservationId);

  const hasSummarySucceededRef = useRef(false);
  useEffect(() => {
    if (summaryStatus === STATUSES.success) {
      hasSummarySucceededRef.current = true;
    }
  }, [summaryStatus]);
  const isInitialSummaryError =
    summaryStatus === STATUSES.error && !hasSummarySucceededRef.current;
  const isSummarySettled =
    !hasCheckIn ||
    summaryStatus === STATUSES.success ||
    summaryStatus === STATUSES.error ||
    hasSummarySucceededRef.current;
  const isInitialSummaryStuck = useSettleWatchdog(
    isSummarySettled,
    SPLASH_TIMING.watchdog,
  );

  const prevOfflineRef = useRef(isOffline);
  useEffect(() => {
    const wasOffline = prevOfflineRef.current;
    prevOfflineRef.current = isOffline;
    if (wasOffline && !isOffline) {
      retryAppLoad();
    }
  }, [isOffline, retryAppLoad]);

  if (isOffline) {
    return <OfflineView />;
  }

  if (isAuthInitializationError && isThrottled) {
    return <ErrorView variant="rate" />;
  }

  if (isRecoveryRoute) {
    return <Outlet />;
  }

  if (!hasTokenInUrl) {
    return <ErrorView />;
  }

  if (isAuthInitializationError) {
    return <ErrorView />;
  }

  if (isAuthPending) {
    return <LoadErrorView />;
  }

  if (isReservationNotFound) {
    return <ReservationUnavailableView />;
  }

  if (isInitialSummaryError || isInitialSummaryStuck) {
    return <LoadErrorView />;
  }

  return <Outlet />;
}

export {ConnectionErrorsHandler};
